import { FileText } from "lucide-react";
import { useLanguage } from "../../i18n/language-provider";
import type { CalendarViewKey } from "../../navigation/calendarViews";
import { navigate } from "../../navigation/history";
import type { AppRoute } from "../../navigation/routes";
import { useShownCalendarView } from "../../navigation/shownCalendarView";
import { useFrontDeskAccess } from "../access/read-only-access";
import { useInsidePhoneMenu } from "../RailFrame";
import { PricesFlyout } from "./PricesFlyout";
import { PricesPhonePanel } from "./PricesPhonePanel";
import { PricesTree } from "./PricesTree";
import {
    currentPricesTab,
    openCalendarView,
    openPricesTab,
    pricesBranchDrawn,
    pricesPlaceLabels,
    type PricesFormProps,
    type PricesPlaceTab,
} from "./prices-places";
import { RailDestination } from "./RailDestination";

interface PricesDestinationProps {
    /** The rail beside the page is folded to its icons; the phone's menu is never. */
    collapsed: boolean;
    /** The route on screen. */
    route: AppRoute;
    /** Prices waits for the property analysis. */
    locked: boolean;
    /** The setup wizard is on screen, whose steps are the place there. */
    onboarding: boolean;
    /** The rail's move, which also closes the phone's menu. */
    navigateTo: (route: AppRoute) => void;
    onMobileClose?: () => void;
    connectionId: number | null | undefined;
    connectionVersion: number;
    /** Another property is open than the one the rail names, so nothing of it is read from the server. */
    openElsewhere: boolean;
}

/**
 * Prices on the rail, and the places inside it: a branch of the three tabs on the open rail, a flyout from the
 * folded icon, tiles and chips in the phone's menu. Only Prices itself where the reader has no other place in it.
 */
export function PricesDestination({
    collapsed,
    route,
    locked,
    onboarding,
    navigateTo,
    onMobileClose,
    connectionId,
    connectionVersion,
    openElsewhere,
}: PricesDestinationProps) {
    const { t } = useLanguage();
    const frontDesk = useFrontDeskAccess();
    const insideMenu = useInsidePhoneMenu();
    const published = useShownCalendarView();
    const labels = pricesPlaceLabels(t);
    const currentTab = currentPricesTab(route);
    const onPrices = route.page === "drafts";
    const selectPrices = () => navigateTo({ page: "drafts" });

    if (!pricesBranchDrawn({ frontDesk, locked, onboarding })) {
        return (
            <RailDestination
                page="drafts"
                label={labels.prices}
                Icon={FileText}
                collapsed={collapsed}
                active={onPrices}
                locked={locked}
                onSelect={selectPrices}
            />
        );
    }

    // What the page last published can outlive the calendar by a commit.
    const shownView = currentTab === "calendar" ? published : null;

    // A move that changes no page leaves the menu open, so it is closed here.
    const openTab = (tab: PricesPlaceTab) => {
        if (openPricesTab(tab, { route, navigateTo }) !== "pushed") onMobileClose?.();
    };
    const openView = (view: CalendarViewKey) => {
        if (openCalendarView(view, { route, navigateTo, navigate, shownView }) !== "pushed") onMobileClose?.();
    };

    const form: PricesFormProps = {
        currentTab,
        shownView,
        labels,
        connectionId,
        connectionVersion,
        readTracking: !frontDesk && !openElsewhere && !locked && connectionId != null,
        onOpenTab: openTab,
        onOpenView: openView,
    };

    if (!insideMenu && collapsed) {
        return <PricesFlyout {...form} active={onPrices} onSelect={selectPrices} />;
    }

    return (
        <>
            <RailDestination
                page="drafts"
                label={labels.prices}
                Icon={FileText}
                collapsed={false}
                active={false}
                ancestor={onPrices}
                onSelect={selectPrices}
            />
            {insideMenu ? <PricesPhonePanel {...form} /> : <PricesTree {...form} />}
        </>
    );
}
